import type {
  CatalogProductStatus,
  CatalogSubmissionStatus,
  DefaultItemRequestStatus,
  DefaultItemStatus,
} from './catalog.js';
import type { VisitStatus } from './clinical.js';
import type { DataExportStatus, PatientImportOutcome } from './data-transfer.js';
import type { PatientPackageState } from './finance.js';
import type { ExpiryState } from './inventory.js';
import type { ProviderReceivableState, ProviderSettlementStatus } from './payment-providers.js';
import type { AppointmentStatus } from './scheduling.js';

/**
 * One status language for every state machine (ADR-016). The web `StatusBadge` reads only these
 * maps; colour is never the only signal (a Persian label is always shown).
 */
export const STATUS_TONES = ['neutral', 'progress', 'attention', 'success', 'failure'] as const;
export type StatusTone = (typeof STATUS_TONES)[number];

export const APPOINTMENT_STATUS_TONE: Readonly<Record<AppointmentStatus, StatusTone>> = {
  scheduled: 'neutral',
  confirmed: 'progress',
  checked_in: 'progress',
  in_progress: 'progress',
  completed: 'success',
  cancelled: 'failure',
  no_show: 'failure',
};

export const VISIT_STATUS_TONE: Readonly<Record<VisitStatus, StatusTone>> = {
  open: 'progress',
  completed: 'success',
  void: 'failure',
};

/** BR-10: expired or within the alert days is red, within the warn days amber. */
export const EXPIRY_STATE_TONE: Readonly<Record<ExpiryState, StatusTone>> = {
  expired: 'failure',
  alert: 'failure',
  warn: 'attention',
  ok: 'neutral',
  none: 'neutral',
};

/** A patient's account: owes the clinic, is owed by it, or is even (ADR-031). */
export const BALANCE_STATES = ['owes', 'credit', 'settled'] as const;
export type BalanceState = (typeof BALANCE_STATES)[number];
export const BALANCE_STATE_TONE: Readonly<Record<BalanceState, StatusTone>> = {
  owes: 'attention',
  credit: 'progress',
  settled: 'success',
};

export const PATIENT_PACKAGE_STATE_TONE: Readonly<Record<PatientPackageState, StatusTone>> = {
  active: 'progress',
  used_up: 'success',
  expired: 'neutral',
  cancelled: 'failure',
};

export const DATA_EXPORT_STATUS_TONE: Readonly<Record<DataExportStatus, StatusTone>> = {
  queued: 'neutral',
  running: 'progress',
  ready: 'success',
  failed: 'failure',
  expired: 'neutral',
};

/** A row of a patient import preview: will be created, already exists, or cannot be read. */
/** A member without a password cannot sign in yet (ADR-033); with one, nothing is shown. */
export const MEMBER_SIGN_IN_STATE_TONE: Readonly<Record<'awaiting_password', StatusTone>> = {
  awaiting_password: 'attention',
};

export const PATIENT_IMPORT_OUTCOME_TONE: Readonly<Record<PatientImportOutcome, StatusTone>> = {
  create: 'success',
  duplicate: 'neutral',
  invalid: 'failure',
};

/**
 * The platform panel's system card (docs/pilot-ops/design.md §2.5): a check that passed, failed,
 * is overdue (the nightly reconciliation older than 36 hours, a job without its beat) or has not
 * run yet on this server.
 */
export const SYSTEM_CHECK_STATES = ['ok', 'failed', 'overdue', 'none'] as const;
export type SystemCheckState = (typeof SYSTEM_CHECK_STATES)[number];
export const SYSTEM_CHECK_TONE: Readonly<Record<SystemCheckState, StatusTone>> = {
  ok: 'success',
  failed: 'failure',
  overdue: 'failure',
  none: 'neutral',
};

/** A GTIN proposal (ADR-055): waiting for the platform, in the catalog, or turned down. */
export const CATALOG_SUBMISSION_STATUS_TONE: Readonly<Record<CatalogSubmissionStatus, StatusTone>> = {
  pending: 'attention',
  approved: 'success',
  rejected: 'failure',
  withdrawn: 'neutral',
};

export const CATALOG_PRODUCT_STATUS_TONE: Readonly<Record<CatalogProductStatus, StatusTone>> = {
  active: 'success',
  retired: 'neutral',
};

export const DEFAULT_ITEM_STATUS_TONE: Readonly<Record<DefaultItemStatus, StatusTone>> = {
  active: 'success',
  removed: 'neutral',
};

export const DEFAULT_ITEM_REQUEST_STATUS_TONE: Readonly<Record<DefaultItemRequestStatus, StatusTone>> = {
  pending: 'attention',
  added: 'success',
  dismissed: 'neutral',
  withdrawn: 'neutral',
};

export const PROVIDER_RECEIVABLE_STATE_TONE: Readonly<Record<ProviderReceivableState, StatusTone>> = {
  open: 'neutral',
  partial: 'progress',
  collected: 'success',
  cancelled: 'neutral',
};

export const PROVIDER_SETTLEMENT_STATUS_TONE: Readonly<Record<ProviderSettlementStatus, StatusTone>> = {
  active: 'success',
  voided: 'neutral',
};
